import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import {
  validateEmail,
  validatePassword,
  validateRequired,
} from '../../utils/validators';
import { RegisterData } from '../../types';
import BusinessCategorySelector from './BusinessCategorySelector';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
  currency: string;
  businessCategoryId: number | null;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
    currency: 'USD',
    businessCategoryId: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};
    const firstNameError = validateRequired(formData.firstName, 'First name');
    const lastNameError = validateRequired(formData.lastName, 'Last name');
    const emailError = validateEmail(formData.email);

    if (firstNameError) newErrors.firstName = firstNameError;
    if (lastNameError) newErrors.lastName = lastNameError;
    if (emailError) newErrors.email = emailError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};
    const passwordError = validatePassword(formData.password);

    if (passwordError) newErrors.password = passwordError;
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: FormErrors = {};
    const orgNameError = validateRequired(
      formData.organizationName,
      'Organization name'
    );

    if (orgNameError) newErrors.organizationName = orgNameError;
    if (!formData.businessCategoryId) {
      newErrors.businessCategoryId = 'Please select a business category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setServerError('');

    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setServerError('');

    if (!validateStep3()) return;

    setLoading(true);
    try {
      const userData: RegisterData = {
        owner_name: `${formData.firstName} ${formData.lastName}`,
        owner_email: formData.email,
        owner_password: formData.password,
        org_name: formData.organizationName,
        business_category_id: formData.businessCategoryId || undefined,
        email: formData.email,
      };

      await register(userData);
      navigate('/login', {
        state: { message: 'Registration successful! Please sign in.' },
      });
    } catch (error) {
      setServerError(String(error) || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Header>
          <h1>Racofin</h1>
          <h2>Create Account</h2>
          <p>Step {step} of 3</p>
        </Header>

        <Form onSubmit={step === 3 ? handleSubmit : handleNextStep}>
          {serverError && <Alert>{serverError}</Alert>}

          {step === 1 && (
            <>
              <FormGroup>
                <label htmlFor="firstName">First Name</label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  $hasError={!!errors.firstName}
                  placeholder="John"
                  autoFocus
                />
                {errors.firstName && <ErrorText>{errors.firstName}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <label htmlFor="lastName">Last Name</label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  $hasError={!!errors.lastName}
                  placeholder="Doe"
                />
                {errors.lastName && <ErrorText>{errors.lastName}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">Email</label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  $hasError={!!errors.email}
                  placeholder="you@example.com"
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FormGroup>
            </>
          )}

          {step === 2 && (
            <>
              <FormGroup>
                <label htmlFor="password">Password</label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  $hasError={!!errors.password}
                  placeholder="••••••••"
                  autoFocus
                />
                {errors.password && <ErrorText>{errors.password}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <label htmlFor="confirmPassword">Confirm Password</label>
                <Input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  $hasError={!!errors.confirmPassword}
                  placeholder="••••••••"
                />
                {errors.confirmPassword && (
                  <ErrorText>{errors.confirmPassword}</ErrorText>
                )}
              </FormGroup>
            </>
          )}

          {step === 3 && (
            <>
              <BusinessCategorySelector
                selectedCategoryId={formData.businessCategoryId}
                onSelect={(categoryId) => {
                  setFormData(prev => ({ ...prev, businessCategoryId: categoryId }));
                  if (errors.businessCategoryId) {
                    setErrors(prev => ({ ...prev, businessCategoryId: undefined }));
                  }
                }}
              />
              {errors.businessCategoryId && (
                <ErrorText style={{ marginTop: '-10px', marginBottom: '10px' }}>
                  {errors.businessCategoryId}
                </ErrorText>
              )}

              <FormGroup>
                <label htmlFor="organizationName">Organization Name</label>
                <Input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  $hasError={!!errors.organizationName}
                  placeholder="Acme Inc."
                />
                {errors.organizationName && (
                  <ErrorText>{errors.organizationName}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <label htmlFor="currency">Base Currency</label>
                <Select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                </Select>
              </FormGroup>
            </>
          )}

          <FormActions>
            {step > 1 && (
              <SecondaryButton
                type="button"
                onClick={() => setStep(step - 1)}
              >
                Back
              </SecondaryButton>
            )}
            <PrimaryButton type="submit" disabled={loading}>
              {loading
                ? 'Creating account...'
                : step === 3
                ? 'Create Account'
                : 'Next'}
            </PrimaryButton>
          </FormActions>
        </Form>

        <Footer>
          <p>
            Already have an account? <StyledLink to="/login">Sign in</StyledLink>
          </p>
        </Footer>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  max-width: 950px;
  width: 100%;
  
  @media (max-width: 768px) {
    max-width: 450px;
    padding: 30px 20px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;

  h1 {
    color: #667eea;
    margin-bottom: 10px;
    font-size: 32px;
  }

  h2 {
    color: #333;
    margin-bottom: 10px;
    font-size: 24px;
  }

  p {
    color: #666;
    font-size: 14px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: 600;
    color: #333;
    font-size: 14px;
  }
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#ddd'};
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#667eea'};
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ErrorText = styled.span`
  color: #ef4444;
  font-size: 12px;
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  background: #f3f4f6;
  color: #333;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const Footer = styled.div`
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: #666;
`;

const StyledLink = styled(Link)`
  color: #667eea;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

export default Register;
